import React from 'react'
import 'katex/dist/katex.min.css'
import katex from 'katex'

interface KaTeXRendererProps {
  content: string
  displayMode?: boolean
  className?: string
  // Đưa ảnh minh hoạ (không phải ảnh công thức) xuống CUỐI → không dính giữa câu dẫn
  moveImagesToEnd?: boolean
}

const KaTeXRenderer: React.FC<KaTeXRendererProps> = ({
  content,
  displayMode = false,
  className = '',
  moveImagesToEnd = false
}) => {
  // Hàm xử lý text thường (Markdown, replacements)
  const processText = (text: string): string => {
    if (!text) return '';
    let processedText = text;

    // Tách ảnh MINH HOẠ ra cuối (giữ ảnh công thức inline) — dùng cho câu dẫn online
    if (moveImagesToEnd) {
      const moved: string[] = [];
      processedText = processedText.replace(/!\[(.*?)\]\((.*?)\)/g, (m, alt) => {
        if (alt === 'Công thức') return m; // ảnh công thức vẫn inline
        moved.push(m);
        return ' ';
      });
      if (moved.length) {
        processedText = processedText.replace(/[ \t]{2,}/g, ' ').trim() + '\n\n' + moved.join('\n');
      }
    }

    // Xử lý Markdown headings
    processedText = processedText.replace(/^(#[\s#]*)(Lý thuyết|Cơ sở lý thuyết|Phân tích|Giải thích|Phân tích câu hỏi|Kết luận|Đáp án|Chọn đáp án)/gmi, '## $2')
    processedText = processedText.replace(/^[\s▸]*(?=#)/gm, '')
    processedText = processedText.replace(/^#+\s*(Lý thuyết|Cơ sở lý thuyết|Phân tích)(.*)$/gmi, '<h3 class="text-sm font-bold text-blue-800 mt-3 mb-1 block flex items-center gap-1"><span class="text-base">📚</span> $1$2</h3>')
    processedText = processedText.replace(/^#+\s*(Giải thích|Phân tích câu hỏi)(.*)$/gmi, '<h3 class="text-sm font-bold text-blue-800 mt-3 mb-1 block flex items-center gap-1"><span class="text-base">💡</span> $1$2</h3>')
    processedText = processedText.replace(/^#+\s*(Kết luận|Đáp án|Chọn đáp án)(.*)$/gmi, '<h3 class="text-sm font-bold text-blue-800 mt-3 mb-1 block flex items-center gap-1"><span class="text-base">✅</span> $1$2</h3>')
    processedText = processedText.replace(/^#+\s*/gm, '')

    // Bold
    processedText = processedText.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-gray-900">$1</strong>')

    // Line breaks
    processedText = processedText.replace(/\n(?!$)/g, '<br />')
    processedText = processedText.replace(/<br \/>\s*<h([1-3])/g, '<h$1')
    processedText = processedText.replace(/<\/(h[1-3])><br \/>/g, '</$1>')

    // Lỗi đánh máy / hiển thị MathType OLE
    processedText = processedText.replace(/(?:\$\$?|\\\[|\\\()?\s*\[!m:\$?(?:obj|math)_[a-zA-Z0-9]+\$?\]\s*(?:\$\$?|\\\]|\\\))?/gi, '<span class="text-xs text-red-500 italic bg-red-50 px-1 rounded">[Lỗi hiển thị công thức]</span>')

    // Hình ảnh markdown — inline-block để không bị ngắt dòng khi nằm trong câu text
    // Phân biệt ảnh công thức (inline nhỏ) và ảnh minh họa (inline lớn hơn)
    processedText = processedText.replace(/!\[(.*?)\]\((.*?)\)/gi, (_, alt, src) => {
      const isFormula = alt === 'Công thức';
      if (isFormula) {
        // Ảnh công thức MathType: inline như chữ, không giới hạn chiều cao cứng
        return `<img src="${src}" alt="${alt}" style="display:inline-block; vertical-align:-0.3em; max-width:100%; max-height:4em;" />`;
      }
      // Ảnh minh họa câu hỏi: inline-block, có thể lớn hơn
      return `<img src="${src}" alt="${alt}" style="display:inline-block; vertical-align:middle; max-width:100%; max-height:200px; margin:2px 4px;" class="rounded border border-slate-200" />`;
    })

    // Greek letters replacement (chỉ áp dụng cho text thường, không phải trong công thức)
    processedText = processedText
      .replace(/\\alpha/g, 'α')
      .replace(/\\beta/g, 'β')
      .replace(/\\gamma/g, 'γ')
      .replace(/\\delta/g, 'δ')
      .replace(/\\epsilon/g, 'ε')
      .replace(/\\theta/g, 'θ')
      .replace(/\\lambda/g, 'λ')
      .replace(/\\mu/g, 'μ')
      .replace(/\\pi/g, 'π')
      .replace(/\\sigma/g, 'σ')
      .replace(/\\omega/g, 'ω')
      .replace(/\\Omega/g, 'Ω')
      .replace(/\\infty/g, '∞')
      .replace(/\\pm/g, '±')
      .replace(/\\times/g, '×')
      .replace(/\\div/g, '÷')
      .replace(/\\leq/g, '≤')
      .replace(/\\geq/g, '≥')
      .replace(/\\neq/g, '≠')
      .replace(/\\approx/g, '≈')
      .replace(/\\rightarrow/g, '→')
      .replace(/\\leftarrow/g, '←')
      .replace(/\\Rightarrow/g, '⇒')
      .replace(/\\Leftarrow/g, '⇐')

    return processedText;
  };

  const renderContent = () => {
    if (!content) return '';

    // Fix CSS conflict with Tailwind (missing square root bar)
    const styleFix = `
      <style>
        .katex-html { border-collapse: initial; }
        .katex .sqrt > .root { border-top: 0.05em solid; }
      </style>
    `;

    try {
      // Strip unparsed MathType OLE objects BEFORE splitting, otherwise KaTeX might try to render it if it's wrapped in $...$
      // This solves the bug where [!m:obj_c5294c9a] or [!m:$obj_c5294c9a$] gets rendered as a subscript _c in KaTeX
      let cleanContent = content.replace(/(?:\$\$?|\\\[|\\\()?\s*\[!m:\$?(?:obj|math)_[a-zA-Z0-9]+\$?\]\s*(?:\$\$?|\\\]|\\\))?/gi, '<span class="text-xs text-red-500 italic bg-red-50 px-1 rounded">[Lỗi hiển thị công thức]</span>');

      // Xóa \n sát cạnh inline formula $...$ để tránh bị chuyển thành <br/> → công thức xuống dòng riêng
      // Dữ liệu cũ từ DB có thể có \n giữa câu dẫn và công thức (do compile từ Word paragraphs)
      cleanContent = cleanContent.replace(/\n(\$(?!\$))/g, ' $1').replace(/(\$(?!\$)[^$]*\$)\n/g, '$1 ');

      // Split content by math delimiters ($$...$$ or $...$)
      // Capturing group () in split includes the separators in the result array
      const parts = cleanContent.split(/(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/g);

      const renderedParts = parts.map((part, index) => {
        if (part.startsWith('$$') && part.endsWith('$$')) {
          // Display Math
          const formula = part.slice(2, -2).trim();
          try {
            return katex.renderToString(formula, {
              displayMode: true,
              throwOnError: false,
              strict: false,
              trust: true,
              fleqn: false
            });
          } catch (e) {
            return `<span class="text-red-500">Error: ${formula}</span>`;
          }
        } else if (part.startsWith('$') && part.endsWith('$')) {
          // Inline Math
          const formula = part.slice(1, -1).trim();
          try {
            return katex.renderToString(formula, {
              displayMode: false,
              throwOnError: false,
              strict: false,
              trust: true
            });
          } catch (e) {
            return `<span class="text-red-500">Error: ${formula}</span>`;
          }
        } else {
          // Normal Text
          return processText(part);
        }
      });

      return styleFix + renderedParts.join('');

    } catch (error) {
      console.error('KaTeX processing error:', error);
      return content;
    }
  };

  // Nếu displayMode = true và không có dấu $, render toàn bộ như công thức
  if (displayMode && !content.includes('$')) {
    try {
      const renderedHTML = katex.renderToString(content, {
        displayMode: true,
        throwOnError: false,
        strict: false
      });
      return (
        <div
          className={`katex-display text-center my-4 ${className}`}
          dangerouslySetInnerHTML={{ __html: renderedHTML }}
        />
      );
    } catch (error) {
      return <div className="text-red-500">{content}</div>;
    }
  }

  return (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: renderContent() }}
    />
  );
}

export default KaTeXRenderer